/**
 * phaseLandscape.js — a read-mostly "phase landscape" modal for a single project, opened from a time
 * entry's phase name in the Time Review modal (and, for managers/leads, from the Time Card modal's
 * "Manage phases" link, so a phase that's hidden from time-entry submission can be turned back on
 * without needing to snooze past the Time Gate to reach it). Shows every phase with its budget, its
 * milestones (posted ones struck-through + greyed), and the same controls as the outline: open/close
 * the phase (Phase Closed Out) and toggle time entry (Allow Time Entries).
 *
 * Markup reuses the EXACT classes PhaseRow/MilestoneRow render in the real outline (ScheduleSection.jsx)
 * — wf-node/wf-row/wf-bullet--phase/wf-bullet--ms/wf-inline-toggle/wf-fee-bar-fill/wf-badge — so this reads
 * as the same outline row, dark-mode included, without actually reusing those components (they're wired
 * into wf/ctx global outline navigation and crew/Slack/checklist loaders that don't apply here, and
 * whose data-fn delegation would fire real outline navigation from inside this modal). The ⋯ menu here
 * is a small self-contained implementation (own click/outside-click handling), not the outline engine's
 * data-fn="openMenu" delegation — it only ever affects the one phase clicked.
 *
 * openPhaseLandscape(projectId, projectName) — projectId is the Airtable Projects record id
 * (time entry's `Project Record UID`). Imperative DOM (matches TimeReviewModal); appended to <body>
 * above whichever modal opened it. All writes go through services/phases.js.
 */
import { getProjectPhases, updatePhaseTimeEntries, closeOutPhase, reopenPhase, saveCloseoutReview } from '../services/phases.js'
import { getProjectMilestones } from '../services/milestones.js'
import { getTargetEbr } from '../services/timecards.js'
import { confirmDialog, alertDialog, choiceDialog } from './Dialog.jsx'
import { ebrBand } from './ebrBands.js'

// Band → plain hex (this modal renders in its own overlay, outside #aj-mgr, so the theme-reactive
// --red/--amber/--green custom properties from timereview.css aren't in scope here — see
// ebrBands.js's own note). Neutral gray matches this file's existing un-themed style. isFixedFee
// matters (see ebrBands.js#ebrBand) — a high EBR on a Fixed-Fee phase is budget headroom, never a
// problem, so it must never render red/amber the way a high Hourly EBR (senior-heavy) does.
function ebrColorHex(ratio, isFixedFee) {
  const band = ebrBand(ratio, isFixedFee)
  if (!band) return '#8a97a0'
  if (band === 'red-high' || band === 'red-low') return '#c0392b'
  if (band === 'amber-high' || band === 'amber-low') return '#b8540a'
  return '#1D9E75'
}

function first(v) { return Array.isArray(v) ? (v[0] || '') : (v || '') }
function num(v) { return v != null && isFinite(Number(v)) ? Number(v) : null }
function r1(v) { const n = num(v); return n != null ? Math.round(n * 10) / 10 : null }

export function openPhaseLandscape(projectId, projectName) {
  if (!projectId) return
  const existing = document.getElementById('aj-phase-landscape')
  if (existing) existing.parentNode.removeChild(existing)

  const overlay = document.createElement('div')
  overlay.id = 'aj-phase-landscape'
  overlay.style.cssText = 'position:fixed;inset:0;z-index:1000002;background:rgba(7,39,45,0.5);display:flex;align-items:center;justify-content:center;font-family:DM Sans,sans-serif'
  overlay.addEventListener('click', function (e) { if (e.target === overlay) close() })

  // Scoped override, not a global outline.css change: .wf-text-wrap is display:flex/nowrap
  // app-wide, which is fine on full-width outline pages but this modal is much narrower — a
  // phase with a long fee/hours/EBR/projection meta string had nowhere to go but overlap the
  // title (confirmed live on Wolford Residence's Schematic Design Phase, 2026-08-08). Let the
  // title and meta wrap onto their own line here without touching every other consumer of these
  // classes.
  const scopedStyle = document.createElement('style')
  scopedStyle.textContent = '#aj-phase-landscape .wf-text-wrap{flex-wrap:wrap;row-gap:2px}' +
    '#aj-phase-landscape .wf-node-text--phase{flex:1 1 180px;min-width:180px}' +
    '#aj-phase-landscape .wf-meta{margin-left:0}'
  overlay.appendChild(scopedStyle)

  const modal = document.createElement('div')
  modal.style.cssText = 'background:var(--aj-bg-page);border-radius:10px;width:980px;max-width:95vw;max-height:88vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 8px 32px rgba(7,39,45,0.22)'

  const head = document.createElement('div')
  head.style.cssText = 'background:#07272D;padding:0.9rem 1.1rem;display:flex;align-items:center;justify-content:space-between;flex-shrink:0'
  const htWrap = document.createElement('div')
  const ht = document.createElement('div'); ht.textContent = 'PHASE LANDSCAPE'
  ht.style.cssText = 'font-family:Montserrat,sans-serif;font-weight:300;font-size:12px;letter-spacing:0.08em;color:#fff'
  const hp = document.createElement('div'); hp.textContent = projectName || ''
  hp.style.cssText = 'font-size:12px;color:rgba(255,255,255,0.55);margin-top:2px'
  htWrap.appendChild(ht); htWrap.appendChild(hp)
  const x = document.createElement('button'); x.textContent = '✕'
  x.style.cssText = 'background:none;border:none;color:rgba(255,255,255,0.6);font-size:18px;cursor:pointer;line-height:1'
  x.addEventListener('click', close)
  head.appendChild(htWrap); head.appendChild(x)

  const body = document.createElement('div')
  body.style.cssText = 'padding:0.5rem 0.75rem;overflow-y:auto;background:var(--aj-bg-page)'
  body.innerHTML = '<div class="wf-loading">Loading phases…</div>'

  modal.appendChild(head); modal.appendChild(body); overlay.appendChild(modal)
  document.body.appendChild(overlay)

  function close() {
    closeMenu()
    const el = document.getElementById('aj-phase-landscape')
    if (el) el.parentNode.removeChild(el)
    document.removeEventListener('keydown', onKey)
  }
  function onKey(e) { if (e.key === 'Escape') close() }
  document.addEventListener('keydown', onKey)

  let phases = [], milestones = [], targetEbr = 125
  function load() {
    return Promise.all([getProjectPhases(projectId), getProjectMilestones(projectId), getTargetEbr()])
      .then(function (r) {
        phases = r[0] || []; milestones = r[1] || []
        if (isFinite(r[2]) && r[2] > 0) targetEbr = r[2]
        render()
      })
      .catch(function () { body.innerHTML = '<div class="wf-loading" style="color:#B7651A">Could not load the project’s phases.</div>' })
  }

  function render() {
    closeMenu()
    while (body.firstChild) body.removeChild(body.firstChild)
    if (!phases.length) {
      const e = document.createElement('div'); e.className = 'wf-loading'
      e.textContent = 'No phases found for this project.'; body.appendChild(e); return
    }
    phases.forEach(function (ph) { body.appendChild(phaseNode(ph)) })
  }

  function phaseNode(ph) {
    const f = ph.fields || {}
    const name = f['Phase Name'] || f['Name'] || 'Phase'
    const phaseIdStr = f['Project Phase ID'] || ''
    const closed = f['Phase Closed Out'] === true
    const allowTime = f['Allow Time Entries'] === true
    const contracted = num(f['Contracted Fee']) != null ? num(f['Contracted Fee']) : num(f['Billed Fee'])
    const feeExp = num(f['Fee Expended'])
    const remaining = num(f['Fee Remaining'])
    const ebr = num(f['Effective Billing Rate'])
    const ebrRatio = (ebr != null && targetEbr > 0) ? ebr / targetEbr : null
    // Canonical isFixedFee check (matches computeLiveFinancials/TimeReviewModal exactly) — a phase
    // only counts as Fixed Fee when it has a REAL Contracted Fee, not the Billed-Fee-fallback
    // `contracted` var below (that fallback is display-only, for the fee bar on old records).
    const isFixedFee = f['Contract Type'] !== 'Hourly' && num(f['Contracted Fee']) > 0
    const projectedBudgetDate = f['Projected Budget Date'] || null
    const hoursBudget = r1(f['Hours Budget']), hoursExp = r1(f['Hours Expended']), hoursRem = r1(f['Hours Remaining'])
    const pendingHours = r1(f['Pending Hours'])

    const truePct = (contracted != null && contracted > 0 && feeExp != null) ? Math.round(feeExp / contracted * 100) : null
    const overBudget = truePct != null && truePct > 100
    const displayPct = truePct != null ? Math.min(truePct, 100) : 0
    const feeColor = overBudget ? '#c0392b' : (truePct != null && truePct > 90) ? '#b8540a' : '#1D9E75'

    const node = document.createElement('div')
    node.className = 'wf-node wf-node--phase' + (closed ? ' wf-node--phase-closed' : '')

    const row = document.createElement('div'); row.className = 'wf-row'; row.style.position = 'relative'
    node.appendChild(row)

    const spacer = document.createElement('span'); spacer.className = 'wf-triangle-spacer wf-triangle-spacer--hidden'
    row.appendChild(spacer)

    const bullet = document.createElement('span'); bullet.className = 'wf-bullet wf-bullet--phase'
    row.appendChild(bullet)

    const textWrap = document.createElement('div'); textWrap.className = 'wf-text-wrap'
    const nameEl = document.createElement('span'); nameEl.className = 'wf-node-text wf-node-text--phase'
    if (phaseIdStr) { const idEl = document.createElement('span'); idEl.className = 'wf-phase-id'; idEl.textContent = phaseIdStr; nameEl.appendChild(idEl) }
    nameEl.appendChild(document.createTextNode(name))
    textWrap.appendChild(nameEl)

    const meta = document.createElement('span'); meta.className = 'wf-meta'
    if (closed) {
      const badge = document.createElement('span'); badge.className = 'wf-badge wf-badge--gray wf-phase-closed-badge'; badge.textContent = 'Closed'
      meta.appendChild(badge)
    } else if (contracted != null && contracted > 0) {
      const barWrap = document.createElement('span'); barWrap.className = 'wf-fee-bar-wrap'
      const barFill = document.createElement('span'); barFill.className = 'wf-fee-bar-fill'
      barFill.style.width = displayPct + '%'; barFill.style.background = feeColor
      barWrap.appendChild(barFill); meta.appendChild(barWrap)
      const pctEl = document.createElement('span'); pctEl.className = 'wf-fee-pct'; pctEl.style.color = feeColor
      pctEl.textContent = (truePct != null ? truePct : 0) + '% fee'; meta.appendChild(pctEl)
      if (overBudget) {
        const overEl = document.createElement('span'); overEl.className = 'wf-fee-pct'; overEl.style.color = '#c0392b'
        overEl.textContent = '$' + Math.round(((feeExp || 0) - contracted) / 1000) + 'k over'; meta.appendChild(overEl)
      } else if (remaining != null && remaining > 0) {
        const remEl = document.createElement('span'); remEl.className = 'wf-fee-rem'
        remEl.textContent = '$' + Math.round(remaining / 1000) + 'k left'; meta.appendChild(remEl)
      }
      if (ebr != null) {
        const ebrEl = document.createElement('span'); ebrEl.className = 'wf-fee-rem'
        const ebrColor = ebrColorHex(ebrRatio, isFixedFee)
        const dot = document.createElement('span')
        dot.style.cssText = 'display:inline-block;width:6px;height:6px;border-radius:50%;margin-right:4px;vertical-align:middle;background:' + ebrColor + ';'
        ebrEl.appendChild(dot)
        ebrEl.style.color = ebrColor
        ebrEl.appendChild(document.createTextNode('EBR $' + Math.round(ebr)))
        meta.appendChild(ebrEl)
        // Budget-runway projection — Fixed-Fee only (see computeLiveFinancials in
        // services/phaseFinancials.js for why Hourly doesn't get one).
        if (!overBudget && projectedBudgetDate) {
          const projD = new Date(projectedBudgetDate + 'T00:00:00')
          const daysOut = Math.round((projD.getTime() - new Date().setHours(0, 0, 0, 0)) / 86400000)
          if (daysOut > 0) {
            const projEl = document.createElement('span'); projEl.className = 'wf-fee-rem'
            projEl.style.color = '#8a97a0'
            projEl.textContent = 'at this pace, budget in ~' + daysOut + 'd (' + projD.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) + ')'
            meta.appendChild(projEl)
          }
        }
      }
    }
    if (hoursBudget != null || hoursExp != null) {
      const hrsEl = document.createElement('span'); hrsEl.className = 'wf-fee-rem'
      hrsEl.textContent = (hoursExp != null ? hoursExp : '?') + ' / ' + (hoursBudget != null ? hoursBudget : '?') + ' hrs' + (hoursRem != null ? ' · ' + hoursRem + ' left' : '')
      // Posted-only, like everywhere else — pendingHours (Pending Review/Submitted/Approved/Post
      // Failed) is a caveat on top, never folded into the budget math itself (see
      // phaseFinancials.js).
      if (pendingHours > 0) hrsEl.textContent += ' (+' + pendingHours + ' pending)'
      meta.appendChild(hrsEl)
    }
    if (!closed) {
      const teLabel = document.createElement('label'); teLabel.className = 'wf-inline-toggle'
      teLabel.title = allowTime ? 'Disable time entries' : 'Enable time entries'
      const te = document.createElement('input'); te.type = 'checkbox'; te.className = 'wf-inline-toggle-inp'; te.checked = allowTime
      const track = document.createElement('span'); track.className = 'wf-inline-toggle-track'
      const thumb = document.createElement('span'); thumb.className = 'wf-inline-toggle-thumb'
      track.appendChild(thumb)
      teLabel.appendChild(te); teLabel.appendChild(track)
      te.addEventListener('change', function () {
        const want = te.checked
        updatePhaseTimeEntries(ph.id, want).then(function () { f['Allow Time Entries'] = want })
          .catch(function () { te.checked = !want; alertDialog({ title: 'Phase', message: 'Could not update time entry.' }) })
      })
      meta.appendChild(teLabel)
      const teText = document.createElement('span'); teText.style.cssText = 'font-size:11px;color:var(--aj-text-muted);margin-left:2px'
      teText.textContent = 'Time entry'
      meta.appendChild(teText)
    }
    textWrap.appendChild(meta)
    row.appendChild(textWrap)

    const menuBtn = document.createElement('button'); menuBtn.className = 'wf-menu-btn'; menuBtn.textContent = '⋯'
    menuBtn.addEventListener('click', function (e) { e.stopPropagation(); toggleMenu(row, ph, f, name, closed) })
    row.appendChild(menuBtn)

    const children = document.createElement('div'); children.className = 'wf-children'
    const msList = milestones.filter(function (m) { return (m.fields['Project Phase'] || '') === name })
    if (!msList.length) {
      const none = document.createElement('div'); none.className = 'wf-loading'; none.style.cssText = 'padding:4px 0 4px 34px;font-style:italic'
      none.textContent = 'No milestones'
      children.appendChild(none)
    } else {
      msList.forEach(function (m) { children.appendChild(milestoneNode(m, closed)) })
    }
    node.appendChild(children)

    return node
  }

  function milestoneNode(m, phaseClosed) {
    const mf = m.fields || {}
    const posted = !!mf['Posted Date']
    const mname = first(mf['Name (from Deliverable)']) || mf['Subtitle'] || mf['Name'] || 'Milestone'
    const node = document.createElement('div')
    node.className = 'wf-node wf-node--ms' + (posted ? ' wf-node--posted' : '') + (phaseClosed ? ' wf-node--ms-readonly' : '')
    const row = document.createElement('div'); row.className = 'wf-row'
    const spacer = document.createElement('span'); spacer.className = 'wf-triangle-spacer wf-triangle-spacer--hidden'
    row.appendChild(spacer)
    const bullet = document.createElement('span'); bullet.className = 'wf-bullet wf-bullet--ms'
    row.appendChild(bullet)
    const textWrap = document.createElement('div'); textWrap.className = 'wf-text-wrap'
    const nameEl = document.createElement('span'); nameEl.className = 'wf-node-text wf-node-text--ms'
    nameEl.textContent = mname
    textWrap.appendChild(nameEl)
    row.appendChild(textWrap)
    node.appendChild(row)
    return node
  }

  // ── ⋯ menu — own small implementation (mirrors .wf-menu/.wf-menu-item styling exactly, so it
  // looks identical to the outline engine's native menus) but NOT wired into the outline engine's
  // global data-fn="openMenu" delegation: this modal's Close-out/Reopen action must only ever touch
  // the one phase clicked here, never trigger real outline navigation. ──
  let openMenuEl = null
  function closeMenu() { if (openMenuEl) { openMenuEl.remove(); openMenuEl = null; document.removeEventListener('click', onDocClick) } }
  function onDocClick(e) { if (openMenuEl && !openMenuEl.contains(e.target)) closeMenu() }
  function toggleMenu(row, ph, f, name, closed) {
    if (openMenuEl) { closeMenu(); return }
    const menu = document.createElement('div'); menu.className = 'wf-menu'
    const item = document.createElement('button'); item.className = 'wf-menu-item'
    item.textContent = closed ? 'Reopen phase' : 'Close out phase'
    item.addEventListener('click', function () { closeMenu(); closed ? doReopen(ph, f) : doClose(ph, f, name) })
    menu.appendChild(item)
    row.appendChild(menu)
    openMenuEl = menu
    document.addEventListener('click', onDocClick)
  }
  async function doClose(ph, f, name) {
    // Gate: a phase can't be closed until every milestone in it is posted.
    const unposted = milestones.filter(function (m) { return (m.fields['Project Phase'] || '') === name && !m.fields['Posted Date'] })
    if (unposted.length) {
      await alertDialog({ title: 'Can’t close this phase yet', message: 'Every milestone in “' + name + '” must be posted first. Still open: ' + unposted.map(function (m) { return first(m.fields['Name (from Deliverable)']) || m.fields['Subtitle'] || m.fields['Name'] || 'a milestone' }).join(', ') + '.' })
      return
    }
    if (!(await confirmDialog({ title: 'Close out phase?', message: 'Close out “' + name + '”? This disables time entry on it.', confirmLabel: 'Close out', danger: true }))) return
    // Live-corrected via enrichPhaseRecords (services/phaseFinancials.js) — safe to read directly,
    // not a frozen rollup.
    const wasOverBudget = f['Phase Fee Status'] === '⚠️ Over Budget'
    closeOutPhase(ph.id).then(function () {
      f['Phase Closed Out'] = true; f['Allow Time Entries'] = false; load()
      if (wasOverBudget) promptCloseoutReview(ph.id, name)
    }).catch(function () { alertDialog({ title: 'Phase', message: 'Could not close the phase.' }) })
  }
  async function doReopen(ph, f) {
    reopenPhase(ph.id, false).then(function () { f['Phase Closed Out'] = false; load() })
      .catch(function () { alertDialog({ title: 'Phase', message: 'Could not reopen the phase.' }) })
  }

  // A phase closing out Over Budget gets a one-question post-mortem — cheap to capture, and it's
  // the dataset that turns "we had one bad phase" into real firm-wide pricing/estimating feedback
  // (see docs/EBR_FRAMEWORK.md's "Phase budget threshold notifications" section, Matt, 2026-08-10).
  // Fires AFTER the close already succeeded — never blocks the actual close-out — and skipping it
  // is a real, silent option (no row written at all): forcing an answer would just train people to
  // pick something at random.
  async function promptCloseoutReview(phaseId, name) {
    const r = await choiceDialog({
      title: '“' + name + '” ran over budget — why?',
      message: 'Helps the firm price and staff similar phases better next time. Skip if you’re not sure yet.',
      choices: [
        { value: 'scope', label: 'Scope crept beyond the contract', description: 'The client asked for more than this phase’s fee covers.' },
        { value: 'pricing', label: 'The phase was underpriced', description: 'The original fee didn’t reflect the real effort, even with normal execution.' },
        { value: 'pace', label: 'It just took longer than it should have', description: 'Scope and price were right; execution ran slow.' },
        { value: 'other', label: 'Something else' },
      ],
      allowNote: true, notePlaceholder: 'Optional context',
      confirmLabel: 'Save', skipLabel: 'Skip',
    })
    if (!r) return
    saveCloseoutReview(phaseId, r.value, r.note || null).catch(function () {})
  }

  load()
}
